import React from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import type { RegionGeoJSON } from "@atlas/shared";
import { cellToRing, decodeCellArray } from "@atlas/shared";
import { MAP_COLORS } from "./map-colors";
import { computeFeatureCollectionBbox } from "../../lib/geo";

// Same style as AtlasMap; token hex values shared via MAP_COLORS (CSS vars can't be used in MapLibre paint)
const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";

function statusColor(geojson: RegionGeoJSON): string {
  const status = geojson.features[0]?.properties.status ?? "not_collected";
  return status === "collected" ? MAP_COLORS.gold : status === "in_progress" ? MAP_COLORS.sky : MAP_COLORS.grey;
}

type CellFC = { type: "FeatureCollection"; features: unknown[] };

function cellsToFC(cells: number[] | null | undefined): CellFC {
  if (!cells || cells.length < 2) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: decodeCellArray(cells).map((c) => ({
      type: "Feature" as const,
      properties: {},
      geometry: { type: "Polygon" as const, coordinates: [cellToRing(c)] },
    })),
  };
}

/**
 * Static boundary preview for a region detail page — status-tinted outline,
 * camera fitted to the polygon. When the viewer's explored cells are passed
 * (spec 13) they are drawn as the fill, always on, instead of the old flat
 * status tint: the map then shows what the "% of area explored" badge means.
 * Anonymous/prerendered views get outline only. Zoom/pan is allowed within
 * the fitted view (see the Map options below); the "View on map" CTA is still
 * the route to the real map for everything else.
 */
export function RegionBoundaryMap({
  geojson,
  exploredCells,
}: {
  geojson: RegionGeoJSON;
  /** Flat [x,y,…] z15 cell array from GET /map/explored; null/undefined = no fill. */
  exploredCells?: number[] | null;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const loadedRef = React.useRef(false);
  const cellsRef = React.useRef<number[] | null | undefined>(exploredCells);
  cellsRef.current = exploredCells;

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Interactive within limits: scroll/pinch zoom in and back out to the
    // fitted view (never further), drag to pan while zoomed in. maxBounds is
    // the fitted viewport, so panning can't wander off the region, and
    // rotation/pitch stay off (this is a preview, not the map page).
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-3.1, 54.5],
      zoom: 8,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      cooperativeGestures: true,
    });
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;

    map.on("load", () => {
      const color = statusColor(geojson);
      map.addSource("region-boundary", {
        type: "geojson",
        data: geojson as maplibregl.GeoJSONSourceSpecification["data"],
      });
      map.addSource("explored-cells", {
        type: "geojson",
        data: cellsToFC(cellsRef.current) as maplibregl.GeoJSONSourceSpecification["data"],
        tolerance: 0,
      });
      // Explored-cells fill (spec 13). Flat 0.3 here rather than the main
      // map's zoom ramp: this preview sits at one fixed zoom and the cells
      // ARE the story, so they can read a touch stronger than the overlay.
      map.addLayer({
        id: "explored-cells-fill",
        type: "fill",
        source: "explored-cells",
        paint: { "fill-color": MAP_COLORS.explored, "fill-antialias": false, "fill-opacity": 0.3 },
      });
      map.addLayer({
        id: "region-line",
        type: "line",
        source: "region-boundary",
        paint: { "line-color": color, "line-width": 2, "line-opacity": 0.9 },
      });

      const bbox = computeFeatureCollectionBbox(geojson);
      if (bbox) {
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 40, duration: 0 });
        // The fitted view is the floor: can't zoom out past it or pan beyond it.
        map.setMinZoom(map.getZoom());
        map.setMaxBounds(map.getBounds());
      }
      loadedRef.current = true;
    });

    return () => {
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
    // Region geometry never changes within a page view; remount handles slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cells usually arrive after the map has loaded (separate query).
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    (map.getSource("explored-cells") as maplibregl.GeoJSONSource | undefined)?.setData(
      cellsToFC(exploredCells) as maplibregl.GeoJSONSourceSpecification["data"]
    );
  }, [exploredCells]);

  return (
    <div
      style={{
        height: 260,
        flexShrink: 0,
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        position: "relative",
      }}
    >
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}
